import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [currentScreen, setCurrentScreen] = useState(0);

  // SEO Configuration
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '懂藏品牌官网 - 重塑时间的权利 | 高端普洱茶年份典藏',
    description: '懂藏,高端普洱茶品牌,专注时间的艺术与茶的陈化。为时代精英提供时间价值认证与人文品味符号的年份茶品牌。',
    keywords: '懂藏,懂藏年份茶,高端普洱茶,陈年普洱,茶叶典藏,国风茶叶,时间艺术,藏茶,品鉴,茶文化',
    canonical: `${siteUrl}/`,
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "懂藏",
      "alternateName": "DONG CANG",
      "description": "高端普洱茶品牌,专注时间的艺术与茶的陈化。为时代精英提供时间价值认证与人文品味符号的年份茶品牌。",
      "url": siteUrl,
      "logo": "https://static.readdy.ai/image/2e1ac11483faca2c19cd44d7d582b015/ce28744b70aae809fa456af269a7a54e.png"
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const screenHeight = window.innerHeight;
      // 修改计算逻辑：当滚动到屏幕的3/4时，就算进入下一屏
      const screen = Math.floor((window.scrollY + screenHeight * 0.25) / screenHeight);
      setCurrentScreen(screen);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const opacity1 = Math.max(0, 1 - scrollY / 400);

  return (
    <div className="bg-[#0A0A0A] text-[#E5E4E2]">
      {/* Screen 1: 进入时间的权利 */}
      <section 
        className="h-screen w-full flex items-center justify-center relative px-6"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/2e1ac11483faca2c19cd44d7d582b015/02b28b175f8754f0da42f3323a65e07b.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto'
        }}
      >
        {/* 毛玻璃效果遮罩 */}
        <div className="absolute inset-0 backdrop-blur-sm" />
        
        <div 
          className="text-center transition-opacity duration-800 relative z-10"
          style={{ opacity: opacity1 }}
        >
          <h1 className="sr-only">懂藏 DONG CANG - 高端普洱茶典藏品牌</h1>
          <img 
            src="https://static.readdy.ai/image/2e1ac11483faca2c19cd44d7d582b015/d833451f82fe9a324567501488e15b91.png"
            alt="懂藏 DONG CANG"
            className="w-[420px] md:w-[600px] lg:w-[750px] mx-auto"
          />
        </div>
        <div className="absolute bottom-12 md:bottom-16 right-6 md:right-16 text-[9px] md:text-[10px] text-[#464443] opacity-60 z-10">
          向下探索时间的深度
        </div>
      </section>

      {/* Screen 2: 品牌宣言与人群定位 */}
      <section className="min-h-screen w-full flex items-center justify-center relative px-6 md:px-16 py-20 md:py-32 bg-[#0A0A0A]">
        {/* 装饰性背景纹理 */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h60v60H0z\' fill=\'none\'/%3E%3Cpath d=\'M30 0v60M0 30h60\' stroke=\'%23D4AF37\' stroke-width=\'0.5\' opacity=\'0.3\'/%3E%3C/svg%3E")',
          }}
        />

        <div className="max-w-[1200px] mx-auto w-full relative z-10">
          {/* 主标题区域 */}
          <div 
            className="text-center mb-16 md:mb-24"
            style={{
              opacity: currentScreen >= 1 ? 1 : 0,
              transform: currentScreen >= 1 ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 1s ease-out'
            }}
          >
            <div className="inline-block">
              <h2 className="text-[18px] md:text-[22px] lg:text-[26px] text-[#D4AF37] font-[500] tracking-wide leading-relaxed mb-6">
                为时代精英提供
              </h2>
              <div className="flex items-center justify-center gap-3 md:gap-4 mb-6">
                <div className="h-[1px] w-12 md:w-16 bg-[#D4AF37]/50" />
                <span className="text-[14px] md:text-[16px] text-[#E5E4E2] tracking-wider">
                  时间价值认证
                </span>
                <div className="h-[1px] w-12 md:w-16 bg-[#D4AF37]/50" />
              </div>
              <h3 className="text-[18px] md:text-[22px] lg:text-[26px] text-[#D4AF37] font-[500] tracking-wide leading-relaxed">
                与人文品味符号
              </h3>
            </div>
          </div>

          {/* 品牌理念区域 */}
          <div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-16 md:mb-20"
            style={{
              opacity: currentScreen >= 1 ? 1 : 0,
              transform: currentScreen >= 1 ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 1s ease-out 0.3s'
            }}
          >
            {/* 左侧：身份定位 */}
            <div className="text-center md:text-left space-y-4">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mx-auto md:mx-0 mb-4">
                <i className="ri-user-star-line text-[24px] md:text-[28px] text-[#D4AF37]" />
              </div>
              <h4 className="text-[12px] md:text-[13px] text-[#D4AF37] tracking-wider mb-3">
                不做批量生产者
              </h4>
              <p className="text-[10px] md:text-[11px] text-[#E5E4E2] leading-relaxed opacity-80 tracking-wide">
                我们拒绝工业化的茶叶生产模式，专注于时间赋予的独特价值
              </p>
            </div>

            {/* 中间：核心定位 */}
            <div className="text-center space-y-4">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-[24px] md:text-[28px] text-[#D4AF37]" />
              </div>
              <h4 className="text-[12px] md:text-[13px] text-[#D4AF37] tracking-wider mb-3">
                只做时间的精选策展人
              </h4>
              <p className="text-[10px] md:text-[11px] text-[#E5E4E2] leading-relaxed opacity-80 tracking-wide">
                从浩如烟海的普洱茶中，严选最具品饮价值与收藏潜力的年份茶品
              </p>
            </div>

            {/* 右侧：价值转化 */}
            <div className="text-center md:text-right space-y-4">
              <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mx-auto md:ml-auto md:mr-0 mb-4">
                <i className="ri-gift-line text-[24px] md:text-[28px] text-[#D4AF37]" />
              </div>
              <h4 className="text-[12px] md:text-[13px] text-[#D4AF37] tracking-wider mb-3">
                转化为时间资产
              </h4>
              <p className="text-[10px] md:text-[11px] text-[#E5E4E2] leading-relaxed opacity-80 tracking-wide">
                通过创新包装和文化赋能，打造可品饮、可收藏、可礼赠的艺术品
              </p>
            </div>
          </div>

          {/* 底部引导文字 */}
          <div 
            className="text-center"
            style={{
              opacity: currentScreen >= 1 ? 1 : 0,
              transform: currentScreen >= 1 ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 1s ease-out 0.6s'
            }}
          >
            <div className="inline-flex items-center gap-4 px-8 py-4 border border-[#D4AF37]/30 rounded-sm">
              <i className="ri-vip-crown-line text-[16px] text-[#D4AF37]" />
              <span className="text-[10px] md:text-[11px] text-[#E5E4E2] tracking-wider opacity-70">
                每一片茶叶，都是时间的见证者
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Screen 3: 首席品鉴官 */}
      <section className="min-h-screen w-full flex flex-col md:flex-row items-center relative py-20 md:py-0">
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100\' height=\'100\' filter=\'url(%23noise)\' opacity=\'0.05\'/%3E%3C/svg%3E")',
          }}
        />
        <div 
          className="w-full md:w-1/2 h-[50vh] md:h-full relative"
          style={{
            opacity: currentScreen >= 2 ? 1 : 0,
            transition: 'opacity 1.2s ease-out'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]" />
          <img 
            src="https://static.readdy.ai/image/2e1ac11483faca2c19cd44d7d582b015/e644a9b483215ccf788d83e7591db2c4.png"
            alt="首席品鉴官"
            className="w-full h-full object-cover object-center opacity-60"
          />
        </div>
        <div 
          className="w-full md:w-1/2 flex items-center justify-center px-6 md:px-24 py-12 md:py-0"
          style={{
            opacity: currentScreen >= 2 ? 1 : 0,
            transform: currentScreen >= 2 ? 'translateX(0)' : 'translateX(50px)',
            transition: 'all 1s ease-out 0.3s'
          }}
        >
          <div className="space-y-8 md:space-y-12">
            <blockquote className="text-[14px] md:text-[16px] text-[#D4AF37] font-[500] tracking-wide leading-relaxed">
              重新定义年份茶的消费体验
            </blockquote>
            <p className="text-[10px] md:text-[11px] text-[#E5E4E2] tracking-wider opacity-80">
              引领茶消费从'藏品'到'饮品'的升级
            </p>
            <div className="text-[9px] md:text-[10px] text-[#E5E4E2] opacity-60 tracking-wide">
              —— 懂藏品牌使命
            </div>
          </div>
        </div>
      </section>

      {/* Screen 4: 进入藏茶室 */}
      <section className="h-screen w-full flex flex-col items-center justify-center px-6">
        <Link
          to="/series"
          className="group relative cursor-pointer"
          style={{
            opacity: currentScreen >= 3 ? 1 : 0,
            transform: currentScreen >= 3 ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 1s ease-out'
          }}
        >
          <div className="w-[100px] h-[100px] md:w-[280px] md:h-[280px] flex items-center justify-center transition-all duration-500 group-hover:scale-105">
            <img 
              src="https://static.readdy.ai/image/2e1ac11483faca2c19cd44d7d582b015/f90798bf81b405c7efd97c32526a4406.png"
              alt="进入线上藏茶室"
              className="w-full h-full object-contain"
            />
          </div>
        </Link>
        <p 
          className="text-[8px] md:text-[9px] text-[#E5E4E2] opacity-50 mt-10 md:mt-12 tracking-wide text-center"
          style={{
            opacity: currentScreen >= 3 ? 0.5 : 0,
            transition: 'opacity 1s ease-out 0.5s'
          }}
        >
          点击进入"藏家会"
        </p>
      </section>
    </div>
  );
}